import { create } from 'zustand'

// The app has unified on a single warm-editorial palette. The theme objects
// are preserved for the Settings picker (which still reads `themes` to render
// options), but `applyTheme()` is now a no-op visually — it only records the
// selection in the Zustand store. The legacy CSS variables it used to stamp
// on :root are now defined as static redirects to the new tokens in index.css.

export interface Theme {
  name:          string
  background:    string
  surface:       string
  accent:        string
  accentLight:   string
  textPrimary:   string
  textSecondary: string
  border:        string
  isDark:        boolean
}

// Only "warm" is selectable in the UI; the rest are visually disabled.
// Keeping the objects here lets the existing picker iterate them unchanged.
export const themes: Record<string, Theme> = {
  warm: {
    name: 'Warm', isDark: false,
    background: '#F5EFE4', surface: '#FFFFFF',
    accent: '#2B4A3A', accentLight: '#E0EBE2',
    textPrimary: '#2B2420', textSecondary: '#9A8B73', border: '#E8DEC8',
  },
  ocean: {
    name: 'Ocean', isDark: false,
    background: '#F0F7FF', surface: '#E1EFFF',
    accent: '#2E86AB', accentLight: '#D6EEFF',
    textPrimary: '#1A2B3C', textSecondary: '#4A6A8A', border: '#C5DEFF',
  },
  sunset: {
    name: 'Sunset', isDark: false,
    background: '#FFF8F0', surface: '#FFE8D6',
    accent: '#E07A5F', accentLight: '#FFE0D6',
    textPrimary: '#2C1810', textSecondary: '#7A4A3A', border: '#FFD5C0',
  },
  forest: {
    name: 'Forest', isDark: false,
    background: '#F2F7F2', surface: '#E3EDE3',
    accent: '#4A7C59', accentLight: '#D6EDD6',
    textPrimary: '#1A2C1A', textSecondary: '#4A6A4A', border: '#C5DCC5',
  },
  lavender: {
    name: 'Lavender', isDark: false,
    background: '#F8F4FF', surface: '#EDE8FF',
    accent: '#7B68EE', accentLight: '#E0D8FF',
    textPrimary: '#1A1040', textSecondary: '#5A4A8A', border: '#D5C8FF',
  },
  pink: {
    name: 'Pink', isDark: false,
    background: '#FFF5F7', surface: '#FFE8ED',
    accent: '#E8698A', accentLight: '#FFD6E0',
    textPrimary: '#2C1A1F', textSecondary: '#7A4A55', border: '#FFCCD5',
  },
}

export const AVAILABLE_THEMES = ['warm'] as const

interface ThemeStore {
  currentTheme: string
  setTheme:     (theme: string) => void
}

export const useThemeStore = create<ThemeStore>(set => ({
  currentTheme: 'warm',
  setTheme:     theme => set({ currentTheme: theme }),
}))

export function applyTheme(themeName: string) {
  // No visual side effects — the warm tokens are static in index.css.
  // Only record the preference so any code listening to the store sees it.
  useThemeStore.getState().setTheme(themeName)
}

export function getTheme(themeName: string): Theme {
  return themes[themeName] ?? themes.warm
}
